// ===============================================
// File: src/contexts/StudioContext.jsx
// Description: Studio profile (Folder OR Drive persistence via StorageContext).
// If no workspace selected -> default in-memory; writes are no-ops.
// Rehydrates after "mudo:vault-restored" and "mudo:files-restored".
// Loads Supabase settings on auth to ensure refresh restores all data.
// ===============================================

import React, { createContext, useContext, useMemo, useState, useEffect, useRef } from "react";
import { useStorage } from "./StorageContext";
import { useVault } from "./VaultContext";
import { useAuth } from "./AuthContext";
import { supabase } from "../supabaseClient";
import { loadStudioSettings, getSettingsCache } from "../lib/studioSettingsStore";

const StudioCtx = createContext(null);
export const useStudio = () => useContext(StudioCtx);

const FILE = "studio/studioProfile.json";
const DEFAULT_STUDIO = { studioId: "default", name: "My Studio", linkedFolder: null };

export function StudioProvider({ children }) {
  const { hasFolder, isDriveLinked, isDriveAuthed, readJSON, writeJSON, workspaceName, hasWorkspace, loadJSON } = useStorage();
  const { user, isAuthReady } = useAuth();

  const [studio, setStudio] = useState(DEFAULT_STUDIO);
  const saveDebounce = useRef(0);
  const bootedRef = useRef(false);
  const supabaseLoadedRef = useRef(false);

  // Dedicated loader with debug logs
  const loadStudioProfile = React.useCallback(async () => {
    try {
      const profile = (typeof loadJSON === "function")
        ? await loadJSON("studio/studioProfile.json", null)
        : await readJSON(FILE, null);
      const next = profile && typeof profile === "object"
        ? { ...DEFAULT_STUDIO, ...profile }
        : DEFAULT_STUDIO;
      setStudio(next);
      return next;
    } catch (err) {
      console.warn("[StudioContext] loadStudioProfile error", err);
      setStudio(DEFAULT_STUDIO);
      return DEFAULT_STUDIO;
    }
  }, [loadJSON, readJSON]);

  // Load Supabase settings on auth to ensure refresh restores customization data
  const loadSupabaseSettings = React.useCallback(async () => {
    try {
      if (!isAuthReady || !user || supabaseLoadedRef.current) return;
      if (!supabase) return;

      // Find user's studio
      const { data: studios, error: studiosError } = await supabase
        .from("studios")
        .select("id, stripe_account_id_resolved, stripe_account_id, stripe_onboarding_complete, stripe_charges_enabled, stripe_payouts_enabled")
        .eq("owner_user_id", user.id)
        .limit(1);

      if (studiosError || !studios || studios.length === 0) {
        return;
      }

      const studioId = studios[0].id;

      // Try cache first for instant load
      const cached = getSettingsCache(studioId);
      if (cached) {
        setStudio((prev) => ({
          ...prev,
          studioId: studioId,
          displayName: cached.businessName || prev?.displayName || "My Studio",
          name: cached.businessName || prev?.name || "My Studio",
          logoUrl: cached.logoUrl || prev?.logoUrl || "",
          website: cached.website || prev?.website || "",
          email: cached.email || prev?.email || "",
          country: cached.country || prev?.country || "Australia",
          region: cached.region || prev?.region || "",
          location: cached.location || prev?.location || "",
          bio: cached.bio || prev?.bio || "",
          practiceTypes: cached.practiceTypes || prev?.practiceTypes || [],
          currency: cached.currency || prev?.currency || "AUD",
          weight: cached.weight || prev?.weight || "g",
          volume: cached.volume || prev?.volume || "ml",
          size: cached.size || prev?.size || "mm",
          productionOutput: cached.productionOutput || prev?.productionOutput || 100,
          theme: cached.theme || prev?.theme || "Burnt Banksia",
          isDarkMode: cached.isDarkMode ?? prev?.isDarkMode,
          // Include Stripe fields
          stripe_account_id_resolved: cached.stripe_account_id_resolved || studios[0].stripe_account_id_resolved || prev?.stripe_account_id_resolved,
          stripe_account_id: cached.stripe_account_id || studios[0].stripe_account_id || prev?.stripe_account_id,
          stripe_onboarding_complete: cached.stripe_onboarding_complete ?? studios[0].stripe_onboarding_complete ?? prev?.stripe_onboarding_complete,
          stripe_charges_enabled: cached.stripe_charges_enabled ?? studios[0].stripe_charges_enabled ?? prev?.stripe_charges_enabled,
          stripe_payouts_enabled: cached.stripe_payouts_enabled ?? studios[0].stripe_payouts_enabled ?? prev?.stripe_payouts_enabled,
        }));
      }

      // Refresh from Supabase in background
      const res = await loadStudioSettings(studioId);
      if (res.ok && res.settings) {
        setStudio((prev) => ({
          ...prev,
          studioId: studioId,
          displayName: res.settings.businessName || prev?.displayName || "My Studio",
          name: res.settings.businessName || prev?.name || "My Studio",
          logoUrl: res.settings.logoUrl || prev?.logoUrl || "",
          website: res.settings.website || prev?.website || "",
          email: res.settings.email || prev?.email || "",
          country: res.settings.country || prev?.country || "Australia",
          region: res.settings.region || prev?.region || "",
          location: res.settings.location || prev?.location || "",
          bio: res.settings.bio || prev?.bio || "",
          practiceTypes: res.settings.practiceTypes || prev?.practiceTypes || [],
          currency: res.settings.currency || prev?.currency || "AUD",
          weight: res.settings.weight || prev?.weight || "g",
          volume: res.settings.volume || prev?.volume || "ml",
          size: res.settings.size || prev?.size || "mm",
          productionOutput: res.settings.productionOutput || prev?.productionOutput || 100,
          theme: res.settings.theme || prev?.theme || "Burnt Banksia",
          isDarkMode: res.settings.isDarkMode ?? prev?.isDarkMode,
          // Include Stripe fields
          stripe_account_id_resolved: res.settings.stripe_account_id_resolved || studios[0].stripe_account_id_resolved || prev?.stripe_account_id_resolved,
          stripe_account_id: res.settings.stripe_account_id || studios[0].stripe_account_id || prev?.stripe_account_id,
          stripe_onboarding_complete: res.settings.stripe_onboarding_complete ?? studios[0].stripe_onboarding_complete ?? prev?.stripe_onboarding_complete,
          stripe_charges_enabled: res.settings.stripe_charges_enabled ?? studios[0].stripe_charges_enabled ?? prev?.stripe_charges_enabled,
          stripe_payouts_enabled: res.settings.stripe_payouts_enabled ?? studios[0].stripe_payouts_enabled ?? prev?.stripe_payouts_enabled,
        }));
      }
      supabaseLoadedRef.current = true;
    } catch (e) {
      console.warn("[StudioContext] loadSupabaseSettings error", e);
    }
  }, [isAuthReady, user?.id]);

  // Load Supabase settings after auth is ready
  useEffect(() => {
    if (isAuthReady && user && !supabaseLoadedRef.current) {
      loadSupabaseSettings();
    }
  }, [isAuthReady, user?.id, loadSupabaseSettings]);

  // hydrate from storage when available, else defaults
  useEffect(() => {
    let alive = true;
    bootedRef.current = false;
    (async () => {
      if (!hasWorkspace) { if (alive) setStudio(DEFAULT_STUDIO); bootedRef.current = true; return; }
      await loadStudioProfile();
      bootedRef.current = true;
    })();
    return () => { alive = false; };
  }, [hasWorkspace, loadStudioProfile]);

  // rehydrate after Receive/MasterReceive with explicit logs
  useEffect(() => {
    const onRestored = async () => {
      if (!hasWorkspace) return;
      console.log("[StudioContext] vault-restored event – reloading studio profile");
      await loadStudioProfile();
    };

    const onFilesRestored = async () => {
      if (!hasWorkspace) return;
      console.log("[StudioContext] files-restored event – reloading studio profile");
      await loadStudioProfile();
    };
    
    const onDisconnect = () => {
      // CRITICAL: Reset to default when workspace disconnected
      setStudio(DEFAULT_STUDIO);
      bootedRef.current = false;
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onFilesRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onFilesRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [hasWorkspace, loadStudioProfile]);

  // persist to storage (debounced) only if workspace linked
  useEffect(() => {
    if (!bootedRef.current || !hasWorkspace) return;
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      try { await writeJSON(FILE, studio); } catch {}
    }, 300);
    return () => clearTimeout(saveDebounce.current);
  }, [studio, hasWorkspace, writeJSON]);

  const api = useMemo(() => ({
    studio,
    setStudio,                   // {studioId, name, linkedFolder, ...}
    // Canonical payouts flag:
    // This studio object's stripe_account_id_resolved is used as the canonical flag
    // for whether payouts are enabled. If present (string), UI treats Stripe
    // as connected; if falsy, UI shows connect prompts and blocks paid publish.
    // Backwards-compatible helpers for ProductionSettings and other consumers
    expectedOutput: studio?.expectedOutput ?? null,
    complexityFactor: studio?.complexityFactor ?? null,
    // updateStudio(patch) updates the in-memory studio object, persists the studio
    // profile, and (when available) also proxies production values into the
    // vaulted `settings` (so Planner and other modules read a single canonical source).
    updateStudio: async (patch = {}) => {
      try {
        // update local state immediately for responsive UI
        setStudio((prev) => {
          const next = { ...(prev || {}), ...patch };
          return next;
        });

        // Persist studio profile to storage (best-effort)
        try {
          const nextProfile = { ...(typeof studio === "object" ? studio : {}), ...(patch || {}) };
          await writeJSON(FILE, nextProfile);
        } catch {}

        // If Vault is available, mirror production-related fields into settings
        try {
          if (vault && typeof vault.setJSON === "function" && typeof vault.getJSON === "function") {
            const cur = (await vault.getJSON("settings", null)) || {};
            const nextSettings = {
              ...cur,
              // map our fields into the centralized settings keys the app expects
              productionOutput:
                patch.expectedOutput !== undefined ? patch.expectedOutput : cur.productionOutput,
              studioComplexityFactor:
                patch.complexityFactor !== undefined ? patch.complexityFactor : cur.studioComplexityFactor,
            };
            await vault.setJSON("settings", nextSettings);
          }
        } catch {}
      } catch (err) {
        // swallow; updateStudio is best-effort and should not crash the UI
      }
    },
    studioId: studio?.studioId || "default",
    linkedFolder: studio?.linkedFolder || null,
    workspaceName,               // handy for showing which workspace is linked
  }), [studio, workspaceName]);

  // Access Vault (must be a hook call; may return null when VaultProvider isn't mounted)
  let vault = null;
  try {
    vault = useVault();
  } catch {
    vault = null;
  }

  // If vaulted settings exist, seed studio-level production fields so
  // consumers of useStudio() (e.g. ProductionSettings) see current values.
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!vault || typeof vault.getJSON !== "function") return;
        const s = await vault.getJSON("settings", null);
        if (!alive || !s) return;
        setStudio((prev) => ({
          ...(prev || {}),
          expectedOutput: prev?.expectedOutput ?? s.productionOutput,
          complexityFactor: prev?.complexityFactor ?? s.studioComplexityFactor,
        }));
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, [vault]);

  return <StudioCtx.Provider value={api}>{children}</StudioCtx.Provider>;
}
